import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "keyboard-chips-band"
}
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "keyboard-matrix" }
const _hoisted_4 = ["onClick"]

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useGameStore } from '@/stores/game'
import { isKioskChannelFromUrl } from '@/utils/channel'
import { useVirtualKeyboard, type VirtualKeyboardLayout } from '@/composables/useVirtualKeyboard'

interface KeyboardKey {
  label: string
  value?: string
  action?: 'delete' | 'clearAll' | 'close' | 'changeCase' | 'toNumber' | 'toAbc' | 'toSpecialSymbols' | 'space'
  colSpan?: number
  rowSpan?: number
  classes?: string[]
}

interface QuickChip {
  label: string
  value: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VirtualKeyboardHost',
  setup(__props) {

const keyboardRef = ref<HTMLElement | null>(null)
const keyboardPosition = ref({ top: 0, left: 0 })
const keyboardWidth = ref(420)
const arrowOffset = ref(20)
const placement = ref<'left' | 'right' | 'below'>('right')
const gameStore = useGameStore()
const {
  isVisible,
  layout,
  isShiftEnabled,
  activeElement,
  anchorSide,
  setKeyboardEnabled,
  setActiveElement,
  setLayout,
  insertText,
  appendIncrement,
  backspace,
  clearValue,
  closeKeyboard,
  toggleShift,
} = useVirtualKeyboard()

const keyboardEnabled = computed(() => {
  const settings = gameStore.getBetSettings
  return Boolean(
    isKioskChannelFromUrl() ||
    settings?.show_screen_keyboard ||
    settings?.channel_id === 3 ||
    settings?.kiosk_mode
  )
})

const anchorSideClass = computed(() => {
  if (placement.value === 'below') return 'anchor-arrow-hidden'
  return placement.value === 'left' ? 'anchor-arrow-after' : 'anchor-arrow-before'
})

const KEYBOARD_LAYOUTS = computed<Record<VirtualKeyboardLayout, KeyboardKey[][]>>(() => {
  const shiftLabel = isShiftEnabled.value ? 'shift on' : 'shift'
  return {
    characters: [
      qwertyRow('qwertyuiop'),
      qwertyRow('asdfghjkl'),
      [
        { label: shiftLabel, action: 'changeCase', colSpan: 2, classes: ['key-highlight'] },
        ...qwertyRow('zxcvbnm'),
        { label: '⌫', action: 'delete', colSpan: 2, classes: ['key-highlight'] },
      ],
      [
        { label: '123', action: 'toNumber', colSpan: 2, classes: ['key-highlight', 'key-abc', 'key-action-trigger'] },
        { label: '#+=', action: 'toSpecialSymbols', colSpan: 2, classes: ['key-highlight', 'key-action-trigger'] },
        { label: 'space', action: 'space', colSpan: 4, classes: ['key-space'] },
        { label: 'clear all', action: 'clearAll', colSpan: 2, classes: ['key-highlight', 'key-clear'] },
      ],
    ],
    alphanumeric: [
      stringRow('1234567890'),
      qwertyRow('qwertyuiop'),
      qwertyRow('asdfghjkl'),
      [
        { label: shiftLabel, action: 'changeCase', colSpan: 2, classes: ['key-highlight'] },
        ...qwertyRow('zxcvbnm'),
        { label: '⌫', action: 'delete', colSpan: 2, classes: ['key-highlight'] },
      ],
      [
        { label: '#+=', action: 'toSpecialSymbols', colSpan: 2, classes: ['key-highlight', 'key-action-trigger'] },
        { label: 'abc', action: 'toAbc', colSpan: 2, classes: ['key-highlight', 'key-abc', 'key-action-trigger'] },
        { label: 'space', action: 'space', colSpan: 4, classes: ['key-space'] },
        { label: 'clear all', action: 'clearAll', colSpan: 2, classes: ['key-highlight', 'key-clear'] },
      ],
    ],
    specialsymbols: [
      stringRow(['[', ']', '{', '}', '#', '%', '^', '*', '+', '=']),
      stringRow(['_', '\'', '|', '~', '<', '>', '€', '$', '£']),
      [
        { label: 'abc', action: 'toAbc', colSpan: 2, classes: ['key-highlight', 'key-action-trigger'] },
        ...stringRow(['.', ',', '?', '!', '-', '/', ':']),
        { label: '⌫', action: 'delete', colSpan: 2, classes: ['key-highlight'] },
      ],
      [
        { label: '123', action: 'toNumber', colSpan: 2, classes: ['key-highlight', 'key-abc', 'key-action-trigger'] },
        { label: 'space', action: 'space', colSpan: 5, classes: ['key-space'] },
        { label: 'clear all', action: 'clearAll', colSpan: 3, classes: ['key-highlight', 'key-clear'] },
      ],
    ],
    numeric: [
      [
        ...stringRow('12345'),
        { label: '.', value: '.', classes: ['key-highlight'] },
        { label: 'OK', action: 'close', rowSpan: 2, classes: ['key-highlight'] },
      ],
      [
        ...stringRow('67890'),
        { label: '⌫', action: 'delete', classes: ['key-highlight'] },
      ],
    ],
    decimal: [
      stringRow('123'),
      stringRow('456'),
      stringRow('789'),
      [
        { label: 'clear all', action: 'clearAll', classes: ['key-highlight', 'key-clear'] },
        { label: '0', value: '0' },
        { label: '⌫', action: 'delete', classes: ['key-highlight'] },
      ],
      [
        { label: '.', value: '.', classes: ['key-highlight'] },
        { label: 'OK', action: 'close', colSpan: 2, classes: ['key-highlight'] },
      ],
    ],
    numericthreeline: [
      stringRow('123'),
      stringRow('456'),
      stringRow('789'),
      [
        { label: 'clear all', action: 'clearAll', classes: ['key-highlight', 'key-clear'] },
        { label: '0', value: '0' },
        { label: '⌫', action: 'delete', classes: ['key-highlight'] },
      ],
      [
        { label: 'OK', action: 'close', colSpan: 3, classes: ['key-highlight'] },
      ],
    ],
  }
})

const quickChips = computed<QuickChip[]>(() => {
  if (layout.value !== 'decimal' && layout.value !== 'numeric' && layout.value !== 'numericthreeline') {
    return []
  }
  return [
    { label: '+1', value: 1 },
    { label: '+10', value: 10 },
    { label: '+100', value: 100 },
  ]
})

const currentRows = computed(() => KEYBOARD_LAYOUTS.value[layout.value] || KEYBOARD_LAYOUTS.value.characters)
const isKeyboardVisible = computed(() => keyboardEnabled.value && isVisible.value)

function stringRow(chars: string | string[]): KeyboardKey[] {
  const values = Array.isArray(chars) ? chars : chars.split('')
  return values.map((value) => ({ label: displayLabel(value), value }))
}

function qwertyRow(chars: string): KeyboardKey[] {
  return chars.split('').map((value) => ({
    label: isShiftEnabled.value ? value.toUpperCase() : value,
    value,
  }))
}

function displayLabel(value: string) {
  if (/^[a-z]$/i.test(value)) {
    return isShiftEnabled.value ? value.toUpperCase() : value.toLowerCase()
  }
  return value
}

function rowColumns(row: KeyboardKey[]) {
  return row.reduce((sum, key) => sum + (key.colSpan || 1), 0)
}

function keyStyle(key: KeyboardKey) {
  return {
    gridColumn: key.colSpan ? `span ${key.colSpan}` : undefined,
    gridRow: key.rowSpan ? `span ${key.rowSpan}` : undefined,
  }
}

function pressKey(key: KeyboardKey) {
  switch (key.action) {
    case 'delete':
      backspace()
      return
    case 'clearAll':
      clearValue()
      return
    case 'close':
      closeKeyboard()
      return
    case 'changeCase':
      toggleShift()
      return
    case 'toNumber':
      setLayout(activeElement.value?.dataset.keyboard === 'decimal' ? 'decimal' : 'numericthreeline')
      return
    case 'toAbc':
      setLayout(activeElement.value?.dataset.keyboard === 'alphanumeric' ? 'alphanumeric' : 'characters')
      return
    case 'toSpecialSymbols':
      setLayout('specialsymbols')
      return
    case 'space':
      insertText(' ')
      return
    default:
      if (key.value) insertText(key.value)
  }
}

function updateKeyboardPosition() {
  if (!isKeyboardVisible.value || !activeElement.value || !keyboardRef.value) return

  const rect = activeElement.value.getBoundingClientRect()
  const measuredWidth = keyboardRef.value.offsetWidth || 420
  const measuredHeight = keyboardRef.value.offsetHeight || 260
  const gap = 12
  const viewportPadding = 12
  const spaceRight = window.innerWidth - rect.right - viewportPadding
  const spaceLeft = rect.left - viewportPadding

  keyboardWidth.value = measuredWidth

  let nextPlacement: 'left' | 'right' | 'below' = 'right'
  let left = rect.right + gap

  if (spaceRight >= measuredWidth + gap) {
    nextPlacement = 'right'
    left = rect.right + gap
  } else if (spaceLeft >= measuredWidth + gap) {
    nextPlacement = 'left'
    left = rect.left - measuredWidth - gap
  } else {
    nextPlacement = 'below'
    left = rect.left + (rect.width / 2) - (measuredWidth / 2)
  }

  left = Math.max(viewportPadding, Math.min(left, window.innerWidth - measuredWidth - viewportPadding))

  let top =
    nextPlacement === 'below'
      ? rect.bottom + gap
      : rect.top + (rect.height / 2) - (measuredHeight / 2)

  if (top + measuredHeight > window.innerHeight - viewportPadding) {
    top = window.innerHeight - measuredHeight - viewportPadding
  }
  top = Math.max(viewportPadding, top)

  placement.value = nextPlacement
  arrowOffset.value = Math.max(20, Math.min(rect.top + rect.height / 2 - top - 16, measuredHeight - 44))
  keyboardPosition.value = { top, left }
}

const keyboardStyle = computed(() => ({
  top: `${keyboardPosition.value.top}px`,
  left: `${keyboardPosition.value.left}px`,
  width: `${keyboardWidth.value}px`,
  '--vk-arrow-offset': `${arrowOffset.value}px`,
}))

const isSupportedInput = (target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement => {
  if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return false
  if (target.disabled || target.readOnly) return false
  if (target instanceof HTMLInputElement) {
    const type = `${target.type || ''}`.toLowerCase()
    if (['hidden', 'checkbox', 'radio', 'file', 'range', 'submit', 'button'].includes(type)) {
      return false
    }
  }
  return true
}

const handleFocusIn = (event: FocusEvent) => {
  if (!keyboardEnabled.value) return
  if (!isSupportedInput(event.target)) return
  setActiveElement(event.target)
}

const handlePointerDown = (event: PointerEvent) => {
  if (!isKeyboardVisible.value) return
  const target = event.target as HTMLElement | null
  if (!target) return
  if (target.closest('.virtual-keyboard')) return
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return
  closeKeyboard()
}

const handleKeydown = (event: KeyboardEvent) => {
  if (!isKeyboardVisible.value) return
  if (event.key === 'Escape') {
    event.preventDefault()
    closeKeyboard()
  }
}

function getPreferredWidth(currentLayout: VirtualKeyboardLayout) {
  if (currentLayout === 'numeric' || currentLayout === 'decimal') return 280
  if (currentLayout === 'numericthreeline') return 240
  if (currentLayout === 'specialsymbols') return 470
  if (currentLayout === 'characters' || currentLayout === 'alphanumeric') return 520
  return 420
}

watch(keyboardEnabled, (enabled) => {
  setKeyboardEnabled(enabled)
}, { immediate: true })

watch([isKeyboardVisible, layout, activeElement], async ([visible]) => {
  if (!visible) return
  keyboardWidth.value = Math.min(getPreferredWidth(layout.value), window.innerWidth - 24)
  await nextTick()
  updateKeyboardPosition()
})

onMounted(() => {
  document.addEventListener('focusin', handleFocusIn)
  document.addEventListener('pointerdown', handlePointerDown)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateKeyboardPosition)
  window.addEventListener('scroll', updateKeyboardPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('focusin', handleFocusIn)
  document.removeEventListener('pointerdown', handlePointerDown)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateKeyboardPosition)
  window.removeEventListener('scroll', updateKeyboardPosition, true)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (isKeyboardVisible.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "keyboardRef",
          ref: keyboardRef,
          class: _normalizeClass(["virtual-keyboard", [
        'kiosk-mode',
        `layout-${_unref(layout)}`,
        anchorSideClass.value,
      ]]),
          style: _normalizeStyle(keyboardStyle.value),
          onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"]))
        }, [
          (quickChips.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(quickChips.value, (chip) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: chip.label,
                    type: "button",
                    class: "keyboard-chip",
                    onClick: ($event: any) => (_unref(appendIncrement)(chip.value))
                  }, _toDisplayString(chip.label), 9, _hoisted_2))
                }), 128))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentRows.value, (row, rowIndex) => {
              return (_openBlock(), _createElementBlock("div", {
                key: `row-${rowIndex}`,
                class: "keyboard-row",
                style: _normalizeStyle({ gridTemplateColumns: `repeat(${rowColumns(row)}, minmax(0, 1fr))` })
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (key, keyIndex) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: `key-${rowIndex}-${keyIndex}-${key.label}`,
                    type: "button",
                    class: _normalizeClass(["keyboard-key", key.classes]),
                    style: _normalizeStyle(keyStyle(key)),
                    onClick: ($event: any) => (pressKey(key))
                  }, _toDisplayString(key.label), 15, _hoisted_4))
                }), 128))
              ], 4))
            }), 128))
          ])
        ], 38))
      : _createCommentVNode("", true)
  ]))
}
}

})